import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import Marquee from 'react-fast-marquee'

import { Carousel } from 'api/fetchers/carousels/fetchCarousel'
import { Cluster } from 'api/fetchers/fetchClusters'
import { useCarousel } from 'api/querries/carousels/useCarousel'
import { useMe } from 'api/querries/me/useMe'
import { useCommunityTags } from 'api/querries/useCommunityTags'
import { usePractitioners } from 'api/querries/usePractitioners'
import mainStyles from 'assets/css/main.module.scss'
import { ReactComponent as GearIcon } from 'assets/icons/gear.svg'
import Bubble from 'components/bubble/Bubble'
import { Button } from 'components/common/button/Button'
import CommunityTagsCarousel from 'components/communityTagsCarousel/CommunityTagsCarousel'
import DefaultLayout from 'components/layout/DefaultLayout'
import { Pagination } from 'components/pagination/Pagination'
import PractitionersCarousel from 'components/practitionersCarousel/PractitionersCarousel'
import PractitionersList, { Sort } from 'components/practitionersList/PractitionersList'
import PractitionersListFilters, {
  PractitionersFiltersType,
} from 'components/practitionersListFilters/PractitionersListFilters'
import { Icon } from 'components/uikit/Icon/Icon'
import { SideModal } from 'components/uikit/SideModal'
import { VisibilityPreferencesSidebar } from 'components/visibilityPreferences/VisibilityPreferences'
import { AnalyticsActionType } from 'types/analytics.type'
//import useDebounce from 'hooks/useDebounce'
import { PractitionerME } from 'types/practitioners'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './CommunitiesOfPractitioners.module.scss'
import BigText from '../components/bigText/BigText'

const DEFAULT_PAGE_SIZE = 10
export interface ActivePractitionersFiltersType {
  cluster: Cluster
  communityTags: string
  favourite: boolean
  company: string
  department: string
  jobFamily: string
  skills4OrAbove: string[]
  experiences4OrAbove: string[]
}

const EMPTY_ME = {} as PractitionerME
const EMPTY_CAROUSEL: Carousel[] = []

export default function CommunitiesOfPractitioners() {
  const containerRef = useRef(null)
  const [activeFilters, setActiveFilters] = useState<Partial<PractitionersFiltersType>>()
  const [sort, setSort] = useState<Sort | undefined>()
  const [currentPage, setCurrentPage] = useState(1)
  const [paginationPageSize, setPaginationPageSize] = useState(DEFAULT_PAGE_SIZE)

  const { data: communityTags } = useCommunityTags()

  const filtersRef = useRef<{ clearAllFilters?: () => void } | null>(null)

  // Removed search debounce because of conditional rendering of empty states
  //const debouncedSearch = useDebounce(activeFilters?.search, 800)

  const { data: meData, isLoading: isMeLoading } = useMe()
  const me = meData ?? EMPTY_ME
  const { data: practitionersData, isLoading } = usePractitioners({
    params: {
      sortBy: sort?.sortingKey ? [sort.sortingKey] : undefined,
      sortDirection: sort?.orientation && sort.orientation,
      communityTagsIds: activeFilters?.communityTags?.length
        ? activeFilters?.communityTags?.map(ct => ct.id)
        : undefined,
      //search: debouncedSearch,
      search: activeFilters?.search,
      clusterIds: activeFilters?.clusterId ? activeFilters.clusterId : undefined,
      skillIds: activeFilters?.skills ? activeFilters.skills : undefined,
      experienceIds: activeFilters?.experiences ? activeFilters.experiences : undefined,
      onlyFavourites: activeFilters?.onlyFavourites || undefined,
      company: activeFilters?.company || undefined,
      jobFamilyName: activeFilters?.jobFamilyName || undefined,
      jobFamilyGroup: activeFilters?.jobFamilyGroup || undefined,
      page: currentPage ? currentPage : 1,
      limit: paginationPageSize || 5,
    },
  })

  const { data: carouselData } = useCarousel()
  const carousel = carouselData ?? EMPTY_CAROUSEL

  const [preferencesModalShow, setPreferencesModalShow] = useState(false)

  // Undefined sets the sort and executes request without sort
  const handleSortingChage = (e: Sort) => setSort(e || undefined)

  // We get practitioners data from /users/me
  const isUserPractitioner = !!me.id

  // This is for the table empty state, when there are no results based on selected filters
  const handleClearFilters = () => {
    if (!filtersRef.current?.clearAllFilters) return
    filtersRef.current?.clearAllFilters()
  }

  useEffect(() => setCurrentPage(1), [activeFilters])

  if (isMeLoading) {
    return <></>
  }
  return (
    <DefaultLayout>
      <div ref={containerRef} className="tour-homepage-connecttoyourcommunities">
        <div className={clsx(styles.containerText)}>
          <div className={clsx(styles.wrapper)}>
            <div className={clsx(styles.content)}>
              <BigText />
              <div
                className={clsx('flex max-w-[1132px] mt-[60px]', 'tour-communityofpractitioners-welcome', {
                  'mx-auto': isUserPractitioner,
                  'ms-48': !isUserPractitioner,
                })}
              >
                <div className="flex flex-col">
                  <div>
                    <div className="max-w-fit relative">
                      <Bubble
                        className="absolute -top-8 right-1/4 translate-x-full"
                        color="#EBFF00"
                        text="Skip the DIY. Get the practitioners."
                        fontClassName="italic font-bold"
                      />
                      <div>
                        <span className={clsx(mainStyles.white2xl)}>
                          Communities <br />
                          of Practitioners
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-[34px] max-w-[700px]">
                    <span className={clsx(mainStyles.whiteH4)}>
                      Connect with practitioners: Storytellers, DT Jedi, Agile Champions, Innovation Champions and
                      Experience Loop Champions who help us learn on the job and enhance our work.
                    </span>
                  </div>
                </div>
                {isUserPractitioner && (
                  <div className="flex flex-grow justify-end items-end flex-col min-w-fit">
                    <div className="flex flex-col items-center">
                      <div className="flex flex-row items-center">
                        <div className="me-2">
                          {me.visibilityPreference.preference === 'partial' ||
                          me.visibilityPreference.preference === 'public' ? (
                            <EyeVisible />
                          ) : (
                            <EyeClosed />
                          )}
                        </div>
                        <div>
                          <p className={clsx(mainStyles.whiteCaptionNormal, 'max-w-[217px] flex-1')}>
                            {me.visibilityPreference.preference === 'private' ? (
                              <>Your profile is not visible on the Communities of Practitioners List</>
                            ) : me.visibilityPreference.preference === 'public' ? (
                              <>Your profile is visible on the Communities of Practitioners List</>
                            ) : (
                              <>Your profile is visible to some users on the Communities of Practitioners List</>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 z-1 -ms-4">
                        <Button
                          text="Preferences"
                          iconRight={<Icon icon={GearIcon} color="white" />}
                          variant="outlined"
                          className="transition-all tour-communityofpractitioners-visibilitypreferences"
                          onClick={() => {
                            setPreferencesModalShow(true)
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: ANALYTICS_EVENTS.VISIBILITY_PREFERENCES_CLICKED,
                            })
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full h-[300px] bg-transparent relative -mt-52">
        <svg width="100%" height="400" viewBox="0 0 100 100" preserveAspectRatio="none" className="-mt-[400px]">
          <path d="M0,100 Q50,0 100,100 Z" fill="white" stroke="white" />
        </svg>
      </div>

      <div className="container flex flex-col justify-center relative">
        <div>
          <p className={clsx(mainStyles.cokeRedH1, mainStyles['text-center'])}>Learn together.</p>
        </div>
        <div className="relative flex justify-center">
          <p className={clsx(mainStyles.cokeRedH1, mainStyles['text-center'])}>Build together.</p>
          <Bubble
            className="top-0 right-[30%] xl:right-[35%] hd:right-[40%] translate-x-full"
            color="#EBFF00"
            text="Your secret weapons"
            fontClassName="italic font-bold -mt-[4px]"
            direction="up"
          />
        </div>
        <div className={clsx(mainStyles.blackH4Normal, mainStyles['text-center'], 'mt-16 mx-auto')}>
          Enjoy the process of creating amazing experiences. These <br />
          practitioners have a common passion and support others by sharing <br />
          what they know to make the work better, together.
        </div>
      </div>
      <CommunityTagsCarousel items={communityTags} />
      <div className="tour-communityofpractitioners-meetsomeofourpractitioners">
        <div>
          <Marquee>
            <p className={styles.marqueeText}>
              Your Network In Action • Your Network In Action • Your Network In Action •
            </p>
          </Marquee>
        </div>
        {carousel?.length > 0 && <PractitionersCarousel items={carousel} />}
      </div>
      <div className="flex max-w-[90%] mx-auto flex-col tour-communityofpractitioners-filteryourpractitioner">
        <div className="mb-12">
          <p className={clsx(mainStyles.cokeRedH1, mainStyles['text-center'])}>Meet All of Our Practitioners</p>
        </div>
        <div>
          <PractitionersListFilters
            ref={filtersRef}
            onChange={(value: Partial<PractitionersFiltersType>) => setActiveFilters(value)}
          />
        </div>
        <div>
          <PractitionersList
            isLoading={isLoading}
            headers={[
              { title: '', key: '' },
              { title: 'Name', key: 'name', sortable: true, sortingKey: 'firstName' },
              { title: 'Company', key: 'company', sortable: true, sortingKey: 'company' },
              { title: 'Community', key: 'community', sortable: true, sortingKey: 'community' },
              { title: 'Department', key: 'department', sortable: true, sortingKey: 'jobFamilyName' },
              { title: 'Job Family', key: 'jobFamily', sortable: true, sortingKey: 'jobFamilyGroup' },
              { title: 'Cluster', key: 'cluster', sortable: true, sortingKey: 'cluster' },
              { title: 'OU', key: 'ou', sortable: true, sortingKey: 'OU' },
              { title: 'Contact', key: 'contact', className: 'justify-center' },
              { title: 'Favorite', key: 'favorite', className: 'justify-center' },
            ]}
            items={practitionersData?.records}
            className="flex-1"
            onHeaderClick={handleSortingChage}
            onFiltersClear={handleClearFilters}
            hasFilters={
              activeFilters &&
              Object.values(activeFilters).filter(f =>
                Array.isArray(f) ? f.length > 0 : f !== '' && f !== undefined && f !== false,
              )?.length > 0
            }
          />
        </div>
        <Pagination
          disabled={!practitionersData?.records?.length}
          page={currentPage}
          allPages={Math.ceil((practitionersData?.total || 0) / paginationPageSize)}
          itemsPerPage={paginationPageSize}
          allItems={practitionersData?.total || 0}
          onSelectPage={page => {
            setCurrentPage(page)
            trackAnalytics({
              type: AnalyticsActionType.action,
              payload: {
                action:
                  page > currentPage
                    ? ANALYTICS_EVENTS.PRACTITIONERS_LIST_PAGINATION_NEXT_CLICKED
                    : ANALYTICS_EVENTS.PRACTITIONERS_LIST_PAGINATION_PREV_CLICKED,

                params: [
                  {
                    key: 'page',
                    value: page?.toString() || '',
                  },
                ],
              },
            })
          }}
          onSelectItemsPerPage={(value: number) => {
            setPaginationPageSize(value)
            setCurrentPage(1)
          }}
        />
      </div>
      <div>
        <div style={{ backgroundColor: 'black' }} className="w-full">
          <svg
            width="100%"
            height="400"
            viewBox="0 0 100 100"
            stroke="white"
            preserveAspectRatio="none"
            className="rotate-180"
          >
            <path d="M0,100 Q50,0 100,100 Z" fill="white" />
          </svg>
        </div>

        <div className="w-full h-[100px] bg-transparent relative -mt-52">
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0,0 Q50,80 100,0 L100,100 L0,100 Z" fill="black" />
          </svg>
        </div>
        <SideModal
          open={preferencesModalShow}
          className={styles.preferencesModal}
          onModalClose={() => setPreferencesModalShow(false)}
        >
          {preferencesModalShow && (
            <VisibilityPreferencesSidebar practitioner={me} onClose={() => setPreferencesModalShow(false)} />
          )}
        </SideModal>
      </div>
    </DefaultLayout>
  )
}

const EyeClosed = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M2.17574 2.17574C1.96272 2.38875 1.94336 2.72208 2.11764 2.95697L2.17574 3.02426L5.40339 6.25192C3.8658 7.33149 2.71662 8.94394 2.23911 10.8515C2.15864 11.173 2.354 11.4988 2.67545 11.5792C2.9969 11.6597 3.32272 11.4644 3.40319 11.1429C3.82677 9.45078 4.87289 8.03158 6.26733 7.11562L7.71503 8.5634C7.14902 9.14063 6.8 9.9314 6.8 10.8037C6.8 12.571 8.23269 14.0037 10 14.0037C10.8723 14.0037 11.6631 13.6547 12.2403 13.0887L16.9757 17.8243C17.2101 18.0586 17.5899 18.0586 17.8243 17.8243C18.0373 17.6113 18.0566 17.2779 17.8824 17.043L17.8243 16.9757L12.9334 12.0844L12.9344 12.0832L11.9743 11.1249L9.67838 8.82942L9.68 8.8288L7.37502 6.52626L7.376 6.5248L6.46937 5.62039L3.02426 2.17574C2.78995 1.94142 2.41005 1.94142 2.17574 2.17574ZM8.56326 9.41238L11.3913 12.2404C11.0313 12.5891 10.5407 12.8037 10 12.8037C8.89543 12.8037 8 11.9083 8 10.8037C8 10.263 8.21459 9.77236 8.56326 9.41238ZM10 4.8C9.19979 4.8 8.42331 4.91846 7.68887 5.14L8.67845 6.12895C9.10709 6.04425 9.54912 6 10 6C13.1385 6 15.8479 8.14421 16.5977 11.1465C16.678 11.468 17.0037 11.6636 17.3252 11.5833C17.6467 11.503 17.8422 11.1773 17.762 10.8558C16.8795 7.32218 13.6924 4.8 10 4.8ZM10.1557 7.60742L13.1968 10.648C13.1154 9.00246 11.7978 7.68611 10.1557 7.60742Z"
      fill="white"
    />
  </svg>
)

const EyeVisible = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M10.0006 8.24357C12.0451 8.24357 13.7026 9.90103 13.7026 11.9456C13.7026 13.9902 12.0451 15.6477 10.0006 15.6477C7.95599 15.6477 6.29852 13.9902 6.29852 11.9456C6.29852 9.90103 7.95599 8.24357 10.0006 8.24357ZM10.0006 9.63183C8.7227 9.63183 7.68679 10.6677 7.68679 11.9456C7.68679 13.2235 8.7227 14.2594 10.0006 14.2594C11.2784 14.2594 12.3143 13.2235 12.3143 11.9456C12.3143 10.6677 11.2784 9.63183 10.0006 9.63183ZM10.0006 5C14.2704 5 17.9563 7.91537 18.9791 12.0009C19.0722 12.3728 18.8461 12.7497 18.4743 12.8428C18.1024 12.9359 17.7254 12.7099 17.6323 12.338C16.7634 8.86675 13.6298 6.38827 10.0006 6.38827C6.36973 6.38827 3.23517 8.86888 2.36775 12.3422C2.27486 12.7142 1.89805 12.9404 1.52611 12.8475C1.15418 12.7546 0.927961 12.3778 1.02085 12.0058C2.04177 7.91788 5.72886 5 10.0006 5Z"
      fill="white"
    />
  </svg>
)
